import { useEffect, useRef, useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { LaurelIcon } from "@/shared/ui/icons/LaurelIcon"
import {
  formatMultiplierDisplay,
  formatTonFromNano,
  formatTonWinner,
  multiplierFromPercent,
  toSafeNumber,
} from "@/shared/lib/pvpRouletteMath"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import tonWonIconUrl from "@/assets/icons/ton-won-icon.png"
import { useI18n } from "@/shared/i18n"
import {
  usePvpRoomPhase,
  usePvpRoomResolvedWinnerMeta,
  usePvpRoomRoundInfo,
} from "../hooks/usePvpRoomSelectors"
import { cn } from "@/shared/lib/utils"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import type { UserGift } from "@/shared/api/types"
import { GiftDetailsSheet } from "@/features/inventory"
import { DepositSheet } from "@/features/wallet"
import { INVENTORY_GIFTS_PARAMS, useUserGifts } from "@/entities/gift"

type ResolvedWinnerMeta = NonNullable<
  ReturnType<typeof usePvpRoomResolvedWinnerMeta>
>

/** Telegram display name comes from `user_data.username` (mapped to BetUserData.username). */
function soloWinnerPrimaryLabel(
  users: WinnerSheetUser[],
  partyUsername: string | undefined,
  t: (key: string) => string
) {
  const u = users[0]
  if (u?.username) return u.username
  if (partyUsername) return partyUsername
  return t("pvpRoom.playerFallback")
}

/**
 * The won bank shown in the sheet, all pre-rake. Composition:
 * - TON = settlement `gross_win_amount` (prize cash, incl. platform-converted
 *   gifts) + winners' returned TON stakes (from their bets — settlement excludes
 *   stake return).
 * - Gifts = won gifts (from losers' bank, via settlement) first, then the
 *   winners' own returned gift stakes (from their bets — these live only in bets).
 *
 * Winner ids come from settlement, so this works uniformly for SOLO and TEAM and
 * regardless of whether the current user is the winner. Order: TON → won → returned.
 * `null` when there is no settlement yet / nothing to show.
 */
function buildPrize(resolvedWinner: ResolvedWinnerMeta): RoundPrize | null {
  const settlement = resolvedWinner.settlement
  if (!settlement || settlement.winners.length === 0) return null

  const winnerIds = new Set(settlement.winners.map((w) => w.userId))
  const winnerBets = resolvedWinner.bets.filter((b) =>
    winnerIds.has(b.userData.userId)
  )

  const grossWinNano = settlement.winners.reduce(
    (sum, w) => sum + w.grossWinNano,
    0
  )
  const returnedTonNano = winnerBets
    .filter((b) => b.assetClass !== "TELEGRAM_GIFT")
    .reduce((sum, b) => sum + (b.tonEquivNano ?? 0), 0)
  const tonNano = grossWinNano + returnedTonNano

  const wonGifts: WinningGift[] = settlement.winners.flatMap((w) =>
    w.wonGifts.map((g) => ({
      id: g.nftId,
      nftId: g.nftId,
      smallImageUrl: g.smallImageUrl ?? null,
      tonEquivNano: g.tonEquivNano,
      name: g.collection || g.model || g.nftId,
    }))
  )
  const returnedGifts: WinningGift[] = winnerBets
    .filter((b) => b.assetClass === "TELEGRAM_GIFT")
    .map((b) => ({
      id: b.betId,
      nftId: b.nftId ?? null,
      smallImageUrl: b.smallImageUrl ?? null,
      tonEquivNano: b.tonEquivNano ?? 0,
      name: b.nftId ?? "",
    }))
  const gifts = [...wonGifts, ...returnedGifts]

  if (tonNano <= 0 && gifts.length === 0) return null

  const giftsNano = gifts.reduce((sum, g) => sum + g.tonEquivNano, 0)
  return { tonNano, gifts, totalNano: tonNano + giftsNano }
}

function buildWinnerSheetViewModel(
  resolvedWinner: ResolvedWinnerMeta,
  t: (key: string) => string
): WinnerSheetViewModel {
  const winnerParty = resolvedWinner.winnerParty ?? null
  const isTeamWinner = resolvedWinner.winnerPartyType === "TEAM"
  const resolvedPrizePool = toSafeNumber(resolvedWinner.prizePoolTon, 0)
  const resolvedMultiplier = multiplierFromPercent(winnerParty?.percent)
  const winnerUsers = resolvedWinner.winnerUsers ?? []
  const maxTeamWinnerAvatars = 10
  const displayedWinnerUsers = winnerUsers.slice(0, maxTeamWinnerAvatars)
  const resolvedWinnerName = soloWinnerPrimaryLabel(
    displayedWinnerUsers,
    winnerParty?.username,
    t
  )

  return {
    isTeamWinner,
    resolvedPrizePool,
    resolvedMultiplier,
    resolvedWinnerName,
    displayedWinnerUsers,
    prize: buildPrize(resolvedWinner),
  }
}

interface ResolvedWinnerSheetProps {
  roomId: string | null
  currentUserId?: string | null
}

export interface WinnerSheetUser {
  userId: string
  username: string | null
  photoUrl: string | null
}

export interface WinningGift {
  /** Stable React key. */
  id: string
  /** NFT id used to look the gift up in the inventory cache for details. */
  nftId: string | null
  smallImageUrl: string | null
  tonEquivNano: number
  name: string
}

export interface RoundPrize {
  /** Final TON before rake (Σ winners' gross_win_amount, incl. platform-converted gifts). */
  tonNano: number
  /** Gifts actually won (platform-taken gifts excluded). */
  gifts: WinningGift[]
  /** Top number = tonNano + Σ gifts.ton_equiv. */
  totalNano: number
}

export interface WinnerSheetViewModel {
  isTeamWinner: boolean
  resolvedPrizePool: number
  resolvedMultiplier: number | null
  resolvedWinnerName: string
  displayedWinnerUsers: WinnerSheetUser[]
  prize: RoundPrize | null
}

function WinnerAvatar({
  user,
  className,
  textClassName,
}: {
  user: WinnerSheetUser
  className: string
  textClassName: string
}) {
  const [hasImageError, setHasImageError] = useState(false)
  const initials = getAvatarInitials(user.username)
  const resolvedPhotoUrl = user.photoUrl
    ? resolveAvatarLoadUrl(user.photoUrl)
    : null
  const shouldShowImage = Boolean(resolvedPhotoUrl) && !hasImageError

  useEffect(() => {
    setHasImageError(false)
  }, [resolvedPhotoUrl])

  return (
    <div className={className}>
      {shouldShowImage ? (
        <img
          src={resolvedPhotoUrl!}
          alt={user.username ?? user.userId}
          className="h-full w-full object-cover"
          onError={() => setHasImageError(true)}
        />
      ) : (
        <span className={textClassName}>{initials}</span>
      )}
    </div>
  )
}

export function ResolvedWinnerSheetBody({
  view,
  onGiftClick,
}: {
  view: WinnerSheetViewModel
  onGiftClick?: (nftId: string) => void
}) {
  const { t } = useI18n()
  const multiplierLabel = formatMultiplierDisplay(view.resolvedMultiplier)
  // Top number: total bank before rake (TON prize + won gifts). Falls back to the
  // snapshot pool when settlement isn't available.
  const prizeTon = view.prize
    ? view.prize.totalNano / 1e9
    : view.resolvedPrizePool
  return (
    <div className="flex w-full flex-col items-center gap-4 bg-[#1F2025] px-4 pb-[calc(24px+var(--app-safe-bottom))] pt-6">
      {view.isTeamWinner ? (
        <>
          <div className="mt-1 flex -space-x-2">
            {view.displayedWinnerUsers.map((u) => (
              <WinnerAvatar
                key={u.userId}
                user={u}
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-[#191C24] bg-[#434750]"
                textClassName="text-xs font-semibold text-white"
              />
            ))}
          </div>
          <span className="text-sm text-white/90">
            {t("pvpRoom.squadWins")}
          </span>
        </>
      ) : (
        <>
          {view.displayedWinnerUsers[0] ? (
            <WinnerAvatar
              user={view.displayedWinnerUsers[0]}
              className="mt-1 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-[#191C24] bg-[#434750]"
              textClassName="text-3xl font-semibold text-white"
            />
          ) : (
            <div className="mt-1 h-24 w-24 overflow-hidden rounded-full border border-[#191C24] bg-[#434750]" />
          )}
          <span className="text-center text-lg font-medium text-white">
            {view.resolvedWinnerName}
          </span>
        </>
      )}

      <div className="mt-2 flex items-end justify-center gap-6">
        <LaurelIcon className="h-[56px] w-[31px]" />
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-1.5">
            <span className="text-[40px] font-bold tabular-nums leading-[48px] tracking-[-1px] text-white">
              {formatTonWinner(prizeTon)}
            </span>
            <TonIcon className="size-7 text-white" />
          </div>
          {multiplierLabel ? (
            <span className="text-[18px] leading-[22px] tracking-[-0.5px] text-white/90">
              x{multiplierLabel}
            </span>
          ) : null}
        </div>
        <LaurelIcon className="h-[56px] w-[31px] scale-x-[-1]" />
      </div>

      {view.prize ? (
        <PrizeGrid prize={view.prize} t={t} onGiftClick={onGiftClick} />
      ) : null}
    </div>
  )
}

/** A single prize tile: square image + price pill + label. */
function PrizeTile({
  imageUrl,
  label,
  tonEquivNano,
  onClick,
}: {
  imageUrl: string | null
  label: string
  tonEquivNano: number
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "flex w-full flex-col items-center gap-1 focus:outline-none",
        onClick && "active:scale-[0.97]"
      )}
    >
      <div className="relative w-full pb-2.5">
        <div
          className="w-full overflow-hidden rounded-[20px] bg-secondary-main bg-cover bg-center [aspect-ratio:1/1]"
          style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
        />
        <span className="absolute bottom-0 left-1/2 flex h-5 -translate-x-1/2 items-center gap-0.5 rounded-full bg-white px-1.5">
          <span className="text-[14px] font-medium leading-4 tracking-[-0.5px] text-black-100">
            {formatTonFromNano(tonEquivNano)}
          </span>
          <TonIcon className="size-3.5 text-black-100" />
        </span>
      </div>
      <span className="line-clamp-1 w-full text-center text-[14px] font-medium leading-4 tracking-[-0.5px] text-white">
        {label}
      </span>
    </button>
  )
}

/**
 * Prize bank grid: TON tile (gross win) + won gifts. 4 per row; up to 8 tiles
 * fit (2 rows) and the rest scroll vertically.
 */
function PrizeGrid({
  prize,
  t,
  onGiftClick,
}: {
  prize: RoundPrize
  t: (key: string) => string
  onGiftClick?: (nftId: string) => void
}) {
  return (
    <div className="mx-auto w-full max-w-[358px] pt-2">
      <div className="grid max-h-[228px] grid-cols-4 gap-x-3 gap-y-3 overflow-y-auto [&::-webkit-scrollbar]:hidden">
        {prize.tonNano > 0 ? (
          <PrizeTile
            imageUrl={tonWonIconUrl}
            label={t("common.ton")}
            tonEquivNano={prize.tonNano}
          />
        ) : null}
        {prize.gifts.map((g) => (
          <PrizeTile
            key={g.id}
            imageUrl={g.smallImageUrl}
            label={g.name}
            tonEquivNano={g.tonEquivNano}
            onClick={
              onGiftClick && g.nftId ? () => onGiftClick(g.nftId!) : undefined
            }
          />
        ))}
      </div>
    </div>
  )
}

export function ResolvedWinnerSheet({
  roomId,
  currentUserId,
}: ResolvedWinnerSheetProps) {
  const { t } = useI18n()
  const phase = usePvpRoomPhase(roomId)
  const roundInfo = usePvpRoomRoundInfo(roomId)
  const resolvedWinner = usePvpRoomResolvedWinnerMeta(roomId)
  const [isOpen, setIsOpen] = useState(false)
  const [latchedView, setLatchedView] = useState<WinnerSheetViewModel | null>(
    null
  )
  const closeTimerRef = useRef<number | null>(null)
  const openedForResolvedRoundIdRef = useRef<string | null>(null)
  const [selectedGift, setSelectedGift] = useState<UserGift | null>(null)
  const [isDepositOpen, setIsDepositOpen] = useState(false)
  const [depositInitialAmount, setDepositInitialAmount] = useState<
    number | null
  >(null)
  const { data: giftsData } = useUserGifts(INVENTORY_GIFTS_PARAMS)

  useEffect(() => {
    if (phase !== "RESOLVED" || !resolvedWinner || !roundInfo?.roundId) {
      return
    }
    const roundId = roundInfo.roundId
    if (openedForResolvedRoundIdRef.current === roundId) {
      return
    }
    openedForResolvedRoundIdRef.current = roundId

    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current)
    }

    setLatchedView(
      buildWinnerSheetViewModel(resolvedWinner, t)
    )
    setIsOpen(true)

    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false)
      setLatchedView(null)
      closeTimerRef.current = null
    }, 3000)
    // Do not clear this timer in a per-deps cleanup: the next snapshot is often the next
    // round (WAITING) milliseconds later; clearing would cancel the 3s presentation.
  }, [phase, roundInfo?.roundId, resolvedWinner, currentUserId, t])

  useEffect(
    () => () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current)
      }
    },
    []
  )

  const displayView = latchedView

  const cancelCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      cancelCloseTimer()
      setLatchedView(null)
    }
  }

  // Look the clicked gift up in the inventory cache; opening details keeps the
  // winner sheet up (cancel auto-close). Won/returned gifts land in the user's
  // inventory once the round resolves, so the cache is the source of full data.
  const handleGiftClick = (nftId: string) => {
    const gift = giftsData?.items.find((g) => g.id === nftId)
    if (!gift) return
    cancelCloseTimer()
    setSelectedGift(gift)
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="overflow-hidden rounded-b-none rounded-t-3xl border-t border-border bg-[#1F2025] p-0 shadow-none"
        >
          <SheetHeader className="px-4 pt-3">
            <SheetTitle className="text-base font-semibold text-foreground">
              {t("pvpRoom.winner")}
            </SheetTitle>
          </SheetHeader>

          {displayView ? (
            <ResolvedWinnerSheetBody
              view={displayView}
              onGiftClick={handleGiftClick}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <GiftDetailsSheet
        open={selectedGift !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedGift(null)
        }}
        gift={selectedGift}
        onRequestDeposit={(shortfallTon) => {
          setDepositInitialAmount(shortfallTon > 0 ? shortfallTon : null)
          setIsDepositOpen(true)
        }}
        // Already on the PvP screen — "Play with this gift" just closes both sheets.
        onPlay={() => {
          setSelectedGift(null)
          handleOpenChange(false)
        }}
      />

      <DepositSheet
        isOpen={isDepositOpen}
        setIsOpen={setIsDepositOpen}
        initialAmount={depositInitialAmount}
        depositUiSource="pvp_resolved_winner"
      />
    </>
  )
}
